import {
  QueryClient,
  UseQueryOptions,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { ViewBooking } from "collins-js-sdk";
import { useCallback, useMemo } from "react";
import { getBookingList } from "../utils/booking";
import { useVenueConfig } from "../hooks/useVenueData";
import { useAppSelector } from "../store/hooks";
import {
  selectDiaryDate,
  selectDiaryFilters,
  toDiaryDateKey
} from "../store/slices/diarySlice";

export const BOOKINGS_LIST_QUERY_KEY_ROOT = "getBookingsList";
export type BookingsListKey = readonly [
  "getBookingsList",
  string | undefined,
  string | undefined
];

export const getBookingsListKey = (
  date: Date | undefined,
  venueConfigId: string | undefined
): BookingsListKey => [
  BOOKINGS_LIST_QUERY_KEY_ROOT,
  toDiaryDateKey(date),
  venueConfigId
];

// Keys handed out by useBookingsList while it shows another date's list as
// placeholder data. A list built from that placeholder must not be stored under
// the new date's key, so writes through these keys are dropped, as they were
// when the list was undefined during the load.
const placeholderKeys = new WeakSet<BookingsListKey>();

export const updateBookingsList = (
  queryClient: QueryClient,
  key: BookingsListKey,
  updater: (current: ViewBooking[] | undefined) => ViewBooking[] | undefined
): ViewBooking[] | undefined => {
  const previous = queryClient.getQueryData<ViewBooking[] | undefined>(key);
  const updated = placeholderKeys.has(key) ? previous : updater(previous);

  if (updated !== undefined && updated !== previous) {
    queryClient.setQueryData(key, updated);
  }

  return previous;
};

// updateBooking() mutates cached bookings with Object.assign, so structural sharing must be disabled.
export const bookingsListQueryOptions = (
  venueConfigId: string | undefined
): Pick<
  UseQueryOptions<ViewBooking[] | undefined>,
  "placeholderData" | "structuralSharing"
> => ({
  structuralSharing: false,
  placeholderData: (previousData, previousQuery) =>
    previousQuery?.queryKey[2] === venueConfigId ? previousData : undefined
});

export const useBookingsList = (): {
  bookings: ViewBooking[] | undefined;
  queryKey: BookingsListKey;
  refetch: () => Promise<void>;
} => {
  const date = useAppSelector(selectDiaryDate);
  const venueConfig = useVenueConfig();
  const queryClient = useQueryClient();
  const venueConfigId = venueConfig?._id;
  const listKey = useMemo(
    () => getBookingsListKey(date, venueConfigId),
    [date, venueConfigId]
  );

  // Cache-read observer only (enabled: false, no queryFn): DiaryPage owns the
  // fetch, so refetch and invalidation only load data while DiaryPage is mounted.
  const query = useQuery({
    queryKey: listKey,
    ...bookingsListQueryOptions(venueConfigId),
    enabled: false
  });
  const { isPlaceholderData } = query;
  const queryKey = useMemo((): BookingsListKey => {
    if (!isPlaceholderData) return listKey;
    const key: BookingsListKey = [listKey[0], listKey[1], listKey[2]];
    placeholderKeys.add(key);
    return key;
  }, [listKey, isPlaceholderData]);
  const refetch = useCallback(
    () => queryClient.refetchQueries({ queryKey, exact: true }),
    [queryClient, queryKey]
  );

  return { bookings: query.data, queryKey, refetch };
};

export const useBookingsFiltered = (): ViewBooking[] | undefined => {
  const { bookings } = useBookingsList();
  const diaryFilters = useAppSelector(selectDiaryFilters);

  return useMemo(
    () =>
      bookings && diaryFilters
        ? getBookingList(
            bookings,
            diaryFilters.timeOfDay,
            diaryFilters.bookingZones,
            diaryFilters.bookingAttributes,
            diaryFilters.bookingTypes,
            diaryFilters.bookingLabels
          )
        : undefined,
    [
      bookings,
      diaryFilters?.timeOfDay,
      diaryFilters?.bookingZones,
      diaryFilters?.bookingAttributes,
      diaryFilters?.bookingTypes,
      diaryFilters?.bookingLabels
    ]
  );
};
